import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import type { ApiJobDescriptionSummary } from '../../libs/api/jobDescriptionService';
import { getApiErrorMessage } from '../../libs/api/errorHandler';
import { fetchCandidatesFromPipeline } from '../candidates/candidateData';
import { buildActiveCandidatesUrl, isActiveCandidateStage } from '../candidates/candidateListUrl';
import { formatVnPhoneForDisplay } from '../candidates/candidateListUtils';

interface JobApplicantsModalProps {
  jd: ApiJobDescriptionSummary | null;
  onClose: () => void;
}

export const JobApplicantsModal: React.FC<JobApplicantsModalProps> = ({ jd, onClose }) => {
  const navigate = useNavigate();
  const jdId = jd?.id;

  const {
    data: applicants = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['jd-applicants', jdId],
    queryFn: () => fetchCandidatesFromPipeline({ jd_id: jdId }),
    enabled: Boolean(jdId),
    select: (items) =>
      items.filter((item) => item.jdId === jdId && isActiveCandidateStage(item.stage)),
  });

  return (
    <Dialog open={Boolean(jd)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl gap-0 p-0">
        <DialogHeader className="border-b border-border-subtle p-5">
          <DialogTitle className="text-text-main">Applicants</DialogTitle>
          <DialogDescription className="text-text-subtle">
            {jd?.title} · {applicants.length} active applicant{applicants.length === 1 ? '' : 's'}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 p-8 text-sm text-text-subtle">
              <Loader2 size={16} className="animate-spin" /> Loading applicants...
            </div>
          ) : error ? (
            <div className="p-8 text-center text-sm text-error">
              {getApiErrorMessage(error, 'Failed to load applicants')}
            </div>
          ) : applicants.length === 0 ? (
            <div className="p-8 text-center text-sm text-text-subtle">
              No active applicants for this job.
            </div>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {applicants.map((applicant) => (
                <li key={applicant.applicationId}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-bg-main"
                    onClick={() =>
                      navigate(`/candidates/${applicant.applicationId}`, {
                        state: { from: '/jobs' },
                      })
                    }
                  >
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-light text-sm font-bold text-primary">
                      {applicant.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-text-main">
                        {applicant.name}
                      </span>
                      <span className="block truncate text-xs text-text-subtle">
                        {formatVnPhoneForDisplay(applicant.phone) || applicant.email || 'N/A'}
                      </span>
                    </span>
                    <span className="shrink-0 rounded-full border border-border-subtle px-2.5 py-0.5 text-xs font-semibold text-text-main">
                      {applicant.stage}
                    </span>
                    <span className="hidden w-20 shrink-0 text-right text-xs text-text-subtle sm:block">
                      {applicant.appliedDate}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="border-t border-border-subtle p-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => jdId && navigate(buildActiveCandidatesUrl(jdId))}
          >
            <ExternalLink size={16} /> Open in Candidates
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
